'use client';

import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource, PortalLink, type MicroContentItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../../_shared/useAsyncResource';

/**
 * The item a popup is showing, and how to close the popup — so any
 * chrome rendered inside a renderer (the shared {@code PopupHeader})
 * can offer the item's category and topic tags as filters without
 * every renderer threading the props by hand.
 */
export interface PopupItemContextValue {
  item: MicroContentItem;
  onClose: () => void;
}

const PopupItemContext = createContext<PopupItemContextValue | null>(null);

export function PopupItemProvider({
  value,
  children,
}: {
  value: PopupItemContextValue | null;
  children: ReactNode;
}) {
  return <PopupItemContext.Provider value={value}>{children}</PopupItemContext.Provider>;
}

export function usePopupItem(): PopupItemContextValue | null {
  return useContext(PopupItemContext);
}

/**
 * Category + topic-tag chips for a micro item — the same affordance the
 * short-video info card has, for every other kind's popup. Each chip
 * opens the kind's listing narrowed to that value (same micro area, the
 * popup closes), so "more like this" is one click from any content.
 */
export function MicroFilterChips({
  item,
  onClose,
  style,
}: {
  item: MicroContentItem;
  onClose: () => void;
  style?: CSSProperties;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const tags = item.topicTags ?? [];
  const categories = useAsyncResource(
    async (signal) => (micro && item.categoryId != null ? micro.categories.list({ signal }) : []),
    [micro, item.categoryId],
  );
  const categoryName =
    categories.kind === 'loaded' && item.categoryId != null
      ? (categories.data.find((c) => String(c.id) === String(item.categoryId))?.name ?? null)
      : null;
  if (!categoryName && tags.length === 0) return null;

  const listing = `/lxp/micro-learning/${encodeURIComponent(item.kindSlug)}`;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, ...style }}>
      {categoryName && item.categoryId != null && (
        <PortalLink
          href={`${listing}?category=${encodeURIComponent(String(item.categoryId))}`}
          onClick={onClose}
          title={t('microVideo.info.moreInCategory', { name: categoryName })}
          style={{ ...chipStyle, gap: 6 }}
        >
          <FolderIcon />
          {categoryName}
        </PortalLink>
      )}
      {tags.map((tag) => (
        <PortalLink
          key={tag}
          href={`${listing}?tag=${encodeURIComponent(tag)}`}
          onClick={onClose}
          title={t('microVideo.info.moreWithTag', { tag })}
          style={chipStyle}
        >
          {tag}
        </PortalLink>
      ))}
    </div>
  );
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: 8,
  border: '1px solid var(--color-border, #d4d4d8)',
  background: 'var(--color-surface-card, #fff)',
  color: 'var(--color-text-primary, #27272a)',
  fontSize: '12px',
  fontWeight: 600,
  textDecoration: 'none',
  maxWidth: '100%',
};

function FolderIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}
